/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Send, CheckCircle2, Loader2 } from "lucide-react";
import { api, extractErr } from "@/lib/api";
import { useT } from "@/i18n";
import Logo from "@/components/Logo";
import LangToggle from "@/components/LangToggle";
import { toast } from "sonner";
import useSEO from "@/hooks/useSEO";

/**
 * Public support / contact page.
 *
 * No login required. User fills name + email + message and the backend
 * forwards it to ADMIN_EMAILS via Resend with Reply-To set to the user
 * so the admin can respond directly. The admin's address is NEVER
 * exposed in the UI. A hidden honeypot field traps simple bot spam.
 */
export default function Support() {
  const { t } = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  // Honeypot — real users never see this, bots will fill it.
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  useSEO({
    title: "Support & Contact — Lumioos",
    description: "Get help with your Lumioos account, billing, AI credits, video generation, image generation or any other workspace feature. Typical reply: under 24 hours.",
    canonical: "https://mylumioos.com/support",
  });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!name.trim() || !email.trim() || message.trim().length < 5) {
      toast.error(t("support_validation") || "Please fill all fields (message must be at least 5 characters).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        page_url: typeof window !== "undefined" ? window.location.href : "",
        website,  // honeypot
      });
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(t("support_rate_limit") || "Too many messages — please try again in an hour.");
      } else {
        toast.error(extractErr(err, t("support_error") || "Could not send. Please try again."));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] text-zinc-950">
      {/* Top bar (mirrors Privacy / Terms pages) */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="md" />
          </Link>
          <div className="flex items-center gap-3">
            <LangToggle />
            <Link
              to="/"
              data-testid="support-back-home-btn"
              className="text-sm font-medium text-zinc-700 hover:text-zinc-950 inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("support_back") || "Back to home"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-16 sm:py-24">
        {/* Eyebrow + heading */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200">
            <MessageCircle className="w-3.5 h-3.5 text-orange-600" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-orange-700">
              {t("support_eyebrow") || "Support"}
            </span>
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl font-black tracking-tighter">
            {t("support_h1") || "How can we help?"}
          </h1>
          <p className="mt-4 text-lg text-zinc-600">
            {t("support_sub") ||
              "Send us a message — bug reports, feature ideas, billing questions, anything. We read every one and reply to the email you give us."}
          </p>
        </div>

        {/* Form OR success state */}
        {sent ? (
          <div
            data-testid="support-sent-card"
            className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-8 text-center"
          >
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <h2 className="mt-4 text-2xl font-black tracking-tight text-emerald-900">
              {t("support_sent_h2") || "Message sent!"}
            </h2>
            <p className="mt-2 text-emerald-800/80 text-sm">
              {t("support_sent_sub") ||
                "Thanks — we'll get back to you at the email you provided, usually within 1 business day."}
            </p>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setName("");
                setEmail("");
                setMessage("");
              }}
              data-testid="support-send-another-btn"
              className="mt-6 inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-md border border-emerald-600 text-emerald-700 hover:bg-emerald-100 transition-colors"
            >
              {t("support_send_another") || "Send another"}
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="support-form"
            className="mt-10 bg-white rounded-2xl border border-zinc-200 p-6 sm:p-8 shadow-sm space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-1.5">
                  {t("support_name") || "Your name"}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={120}
                  data-testid="support-name-input"
                  className="w-full px-4 py-3 rounded-md border border-zinc-300 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100 text-sm"
                  placeholder={t("support_name_ph") || "John Doe"}
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-1.5">
                  {t("support_email") || "Your email"}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  maxLength={200}
                  data-testid="support-email-input"
                  className="w-full px-4 py-3 rounded-md border border-zinc-300 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100 text-sm"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-1.5">
                {t("support_message") || "Message"}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                minLength={5}
                maxLength={4000}
                rows={7}
                data-testid="support-message-input"
                className="w-full px-4 py-3 rounded-md border border-zinc-300 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100 text-sm resize-y"
                placeholder={t("support_message_ph") || "Tell us what's going on…"}
              />
              <div className="mt-1 text-[11px] text-zinc-400 text-right">
                {message.length}/4000
              </div>
            </div>

            {/* Honeypot — hidden from real users via inline style. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
              <label>
                Website (leave empty)
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </label>
            </div>

            <div className="flex items-center justify-between gap-4 pt-2">
              <div className="text-[11px] text-zinc-500">
                {t("support_privacy_note") || "Your message goes to our team. We never share your email."}
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="support-submit-btn"
                className="btn-accent inline-flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("support_sending") || "Sending…"}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t("support_send") || "Send message"}
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* SEO / Help context — gives the page meaningful crawlable copy so
            it stops looking like an empty form to search engines. Hidden
            from screen readers (sr-only equivalent) is NOT used — we want
            this surfaced for visitors too. */}
        <section className="mt-16 text-zinc-700 text-[14px] leading-7 space-y-6">
          <div>
            <h2 className="text-xl font-black text-zinc-900 mb-2">What you can ask us about</h2>
            <p>
              Lumioos support helps with everything related to your AI workspace: billing and subscription
              changes, credit balances, video and image generation jobs that didn't complete, CV builder
              exports, interview practice transcripts, Game Studio crashes, mobile app sign-in issues, and
              feature requests. If you're hitting a paywall and think it's a bug, mention your plan and we'll
              fix the credit count manually.
            </p>
          </div>
          <div>
            <h2 className="text-xl font-black text-zinc-900 mb-2">Response time</h2>
            <p>
              We respond in under 24 hours on weekdays, often faster. Power plan users get priority on the
              queue — flag your plan in the message and we'll route it directly. For urgent production-down
              issues, send a screenshot of the error and the affected URL.
            </p>
          </div>
          <div>
            <h2 className="text-xl font-black text-zinc-900 mb-2">Before you write</h2>
            <p>
              Two quick fixes that solve most reports: (1) reload the page in a normal browser tab (not a
              cached PWA shortcut) and re-sign-in, which clears 90% of stale-session bugs. (2) Check your
              plan's credit balance under Billing — many "feature not working" reports are actually empty
              credit buckets that just need a top-up.
            </p>
          </div>
          <div>
            <h2 className="text-xl font-black text-zinc-900 mb-2">Other ways to reach us</h2>
            <p>
              Status updates on outages live on our X / Twitter account. For account deletion or GDPR data
              export requests, use the form above and include the phrase "data export" or "delete account" —
              we handle those manually within 7 days. We never share your email or message content with
              anyone outside the Lumioos team.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
